import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { KeyRound } from "lucide-react";

/**
 * /members — invitation-only landing per Build Guide Phase 2. Members are
 * NOT a marketing target on the public site; this page exists only so
 * existing/bookmarked links don't 404. Reinforces the "invitation-only"
 * positioning and points members to the Sign In page.
 */
export default function MembersMarketingPage() {
  return (
    <MarketingLayout>
      <section data-testid="members-marketing-page" className="relative">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-14 py-24 sm:py-32 text-center">
          <div className="mx-auto w-14 h-14 flex items-center justify-center bg-[#1A422D] text-[#D9C68A] rounded-full">
            <KeyRound size={26} />
          </div>
          <div className="mt-8 text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
            Members
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] mt-4 leading-[1.05]">
            Invitation-only, by design.
          </h1>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            My Crest Club is offered exclusively through participating private clubs. If your club uses our platform, they&rsquo;ll invite you personally — no public sign-up required.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/login"
              data-testid="members-signin-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors"
            >
              Sign In <span aria-hidden>→</span>
            </Link>
            <Link
              to="/clubs"
              data-testid="members-clubs-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#1A422D] text-[#1A422D] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
            >
              Is your club a member?
            </Link>
          </div>

          <div className="mt-14 text-sm text-[#7A7A7A]">
            Not yet invited? Ask your club pro shop to enable My Crest Club for your membership.
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

// Legacy named export kept for /clubs, /pricing, /about which import
// ComingSoonSection from this file. Same visual language, minus the
// members-specific copy.
export function ComingSoonSection({ eyebrow, headline, blurb, testId }) {
  return (
    <section data-testid={testId} className="relative">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-14 py-24 sm:py-32">
        <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
          {eyebrow}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] mt-4 leading-[1.05]">
          {headline}
        </h1>
        <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed max-w-3xl">
          {blurb}
        </p>

        <div className="mt-10 inline-flex items-center gap-2 px-4 py-2 bg-[#D9C68A]/20 border border-[#B59A5A]/40 text-[11px] uppercase tracking-[0.24em] font-semibold text-[#8A6A2A]">
          Full page coming soon
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors"
          >
            Sign In <span aria-hidden>→</span>
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#1A422D] text-[#1A422D] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
          >
            Schedule Demo
          </Link>
        </div>
      </div>
    </section>
  );
}
